var _effectorFileName = "/src/shared/lib/effector/page/page.ts";
import { ParsedUrlQuery } from 'querystring';
import { GetServerSidePropsContext } from 'next';
import { createEvent, createStore, sample, Unit } from 'effector';
import { removeUndefined } from '../../remove-undefined';
export const pageStarted = createEvent<{
  pageType: string;
  pageCtx: unknown;
}>({
  loc: {
    file: _effectorFileName,
    line: 7,
    column: 27
  },
  name: "pageStarted",
  sid: "-2ydcxz"
});
const $currentPage = createStore<string | null>(null, {
  loc: {
    file: _effectorFileName,
    line: 12,
    column: 21
  },
  name: "$currentPage",
  sid: "2ms0h7"
}).on(pageStarted, (_, {
  pageType
}) => pageType);
export interface PageContext<Q extends ParsedUrlQuery, P> {
  query?: Q | null;
  params?: P | null;
  url?: string;
}
export const getPageContext = <Q extends ParsedUrlQuery, P>(context: GetServerSidePropsContext): PageContext<Q, P> => {
  return removeUndefined<PageContext<Q, P>>({
    query: (context.query ?? {}) as Q,
    params: (context.params ?? {}) as P,
    url: context.resolvedUrl
  });
};
export function declarePage<Ctx = PageContext<any, any>>(config: {
  pageType: string;
  contextContract?: {
    isData: (data: unknown) => data is Ctx;
  };
}) {
  const open = createEvent<Ctx>({
    loc: {
      file: _effectorFileName,
      line: 34,
      column: 15
    },
    name: "open",
    sid: "7ybly8"
  });
  const $ctx = createStore<Ctx | null>(null, {
    and: {
      /**
       * Custom human-readable sid for easier debugging
       *
       * Completely optional practice, can be changed to factories field at
       * effector's babel/swc plugin configuration like this:
       * ```json
       *  factories: ["#root/shared/app"]
       * ```
       *
       * See the docs on SIDs: https://effector.dev/docs/api/effector/babel-plugin#sid
       */
      sid: `pageCtx:${config.pageType}`
    },
    loc: {
      file: _effectorFileName,
      line: 36,
      column: 15
    },
    name: "$ctx",
    sid: "kzze11"
  });
  sample({
    and: [{
      clock: open,
      filter: rawCtx => {
        if (config.contextContract) {
          return config.contextContract.isData(rawCtx);
        }

        /**
         * Skip as-is, if no contract is provided
         */

        return true;
      },
      target: [$ctx, pageStarted.prepend((ctx: Ctx) => ({
        pageCtx: ctx,
        pageType: config.pageType
      }))]
    }],
    or: {
      loc: {
        file: _effectorFileName,
        line: 51,
        column: 2
      },
      sid: "-9j0m97"
    }
  });
  const $active = $currentPage.map(page => page === config.pageType);
  const activated = sample({
    and: [{
      clock: $active,
      filter: Boolean
    }],
    or: {
      loc: {
        file: _effectorFileName,
        line: 74,
        column: 20
      },
      name: "activated",
      sid: "6nbe77"
    }
  });
  const deactivated = sample({
    and: [{
      clock: $active,
      filter: active => !active
    }],
    or: {
      loc: {
        file: _effectorFileName,
        line: 78,
        column: 22
      },
      name: "deactivated",
      sid: "-byoma"
    }
  });
  const opened = createEvent<Ctx>({
    loc: {
      file: _effectorFileName,
      line: 83,
      column: 17
    },
    name: "opened",
    sid: "-oz21bv"
  });
  const closed = createEvent<Ctx>({
    loc: {
      file: _effectorFileName,
      line: 84,
      column: 17
    },
    name: "closed",
    sid: "-mxydbz"
  });
  sample({
    and: [{
      clock: activated,
      source: $ctx,
      /**
       * Type assertion is totally fine here, as we know that $ctx will be of Ctx type at this moment,
       * because it is the way this logic is written and also here we have a contract check for that
       *
       * We can't (yet?) express it in the TypeScript type system though, hence the `as Ctx` cast
       *
       * In real-world apps it is better to write some tests for such cases, rather than
       * writing more complicated code to satisfy the type inference system
       */
      fn: ctx => ctx as Ctx,
      target: opened
    }],
    or: {
      loc: {
        file: _effectorFileName,
        line: 86,
        column: 2
      },
      sid: "-814s3t"
    }
  });
  sample({
    and: [{
      clock: deactivated,
      source: $ctx,
      fn: ctx => ctx as Ctx,
      target: closed
    }],
    or: {
      loc: {
        file: _effectorFileName,
        line: 102,
        column: 2
      },
      sid: "owdrs"
    }
  });
  sample({
    and: [{
      clock: closed,
      fn: () => null,
      target: $ctx
    }],
    or: {
      loc: {
        file: _effectorFileName,
        line: 109,
        column: 2
      },
      sid: "sqxxb"
    }
  });
  return {
    open,
    opened,
    closed,
    $active
  };
}
export const onlyPage = <T,>(unit: Unit<T>, pageType: string) => {
  return sample({
    and: [{
      clock: unit,
      source: $currentPage,
      filter: page => page === pageType
    }],
    or: {
      loc: {
        file: _effectorFileName,
        line: 124,
        column: 9
      },
      sid: "1o37wx"
    }
  });
};